import {
  Button,
  ConfirmModal,
  CustomPageContainer,
  FilterSearchBar,
  Table,
  TablePaginationContext,
} from '@/components';
import { TableRow, TableSort } from '@/components/Table/types';
import { deleteRowColumnConfig } from '@/utils';
import {
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetInfrastructureCosts } from '../../queries';
import { useDeleteInfrastructureCost } from '../../queries/delete-infrastructure-cost';
import { CreateInfrastructureCostModal } from './components/CreateInfrastructureCostModal/CreateInfrastructureCostModal';
import {
  getInfrastructureCostColumns,
  getInfrastructureCostColumnsNamedMapped,
} from './utils';
import { queryClient } from '@/constants';
import { InfrastructureCostKey } from '../../queries/types';

export const InfrastructureCostPage = (): ReactElement => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [costSelected, setCostSelected] = useState<number>();
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [tableSort, setTableSort] = useState<TableSort[]>([]);
  const navigate = useNavigate();

  const handleModalState = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen);
  }, []);

  const { searchTerm, page, onPageChange, handleSearchChange } = useContext(
    TablePaginationContext,
  );

  const { mutate: deleteInfrastructureCost } = useDeleteInfrastructureCost();

  const handleOpenRemoveCostModalState = useCallback((id: number) => {
    setIsOpenDelete(true);
    setCostSelected(id);
  }, []);

  const handleCloseRemoveCostModalState = useCallback(() => {
    setIsOpenDelete(false);
    setCostSelected(undefined);
  }, []);

  const handleDeleteCost = useCallback(() => {
    if (!costSelected) {
      return;
    }

    deleteInfrastructureCost(costSelected, {
      onError: () => {
        toast.error(t('error_deleting_infrastruture_cost'));
      },
      onSuccess: () => {
        queryClient.invalidateQueries(InfrastructureCostKey.ALL);
        toast.success(t('success_delete_infrastructure_cost'));
      },
    });

    handleCloseRemoveCostModalState();
  }, [
    costSelected,
    deleteInfrastructureCost,
    handleCloseRemoveCostModalState,
    t,
  ]);

  const {
    data: infrastructureCosts,
    isFetching: isLoadinginfrastructureCosts,
    isError,
  } = useGetInfrastructureCosts(
    page,
    searchTerm,
    getInfrastructureCostColumnsNamedMapped(tableSort?.[0]?.columnId ?? 'name'),
    tableSort?.[0]?.direction ?? 'ascending',
  );

  useEffect(() => {
    if (isError) {
      toast.error(t('error_getting_infrastructureCosts'));
    }
  }, [isError, t]);

  const routeChange = (row?: TableRow) => {
    const path = `${row?.id}`;
    navigate(path);
  };

  const onSort = (data: TableSort[]) => {
    setTableSort(data);
  };

  return (
    <CustomPageContainer>
      <CustomPageContainer.Header title={t('infrastructure_costs')} />
      <FilterSearchBar
        total={infrastructureCosts?.totalRows || 0}
        title={t('costs')}
        variant="secondary"
        onSearch={handleSearchChange}
        otherActions={
          <Button
            onClick={handleModalState}
            variant="primary"
            className="px-5 py-2.5"
          >
            {t('add')}
          </Button>
        }
      />
      <Table
        columns={[
          ...getInfrastructureCostColumns({
            onClick: routeChange,
          }),
          deleteRowColumnConfig({
            disabledMessage: t('disabled_delete_infrastructure_cost'),
            onDeleteClick: (row) =>
              handleOpenRemoveCostModalState(row.id as number),
          }),
        ]}
        rows={infrastructureCosts?.items ?? []}
        isLoading={isLoadinginfrastructureCosts}
        noResultsLabel={t('no_costs_available')}
        withPagination
        totalItems={infrastructureCosts?.totalRows}
        totalPages={infrastructureCosts?.totalPages}
        currentPage={page}
        onPageChange={onPageChange}
        onSort={onSort}
      />
      {isOpen && (
        <CreateInfrastructureCostModal
          handleClose={handleModalState}
          isOpen={isOpen}
        />
      )}
      <ConfirmModal
        isOpen={isOpenDelete}
        handleClose={handleCloseRemoveCostModalState}
        title={t('please_confirm_action')}
        message={t('confirm_infrastructure_cost_delete')}
        onConfirm={handleDeleteCost}
      />
    </CustomPageContainer>
  );
};
